/**
 * Work orders (Lumen-BE 0092, `modules/work`): assign and track work at a site or cabinet.
 *
 * Who sees and changes what is the API's call, and each work order says what THIS caller may do
 * (`can`), so the console offers exactly the controls the API will accept: a holder of only
 * `work.update` sees their own work and moves its status, ticks its checklist, describes it,
 * comments and attaches; `work.manage` does everything.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, request } from './client';
import { StorageUploadError } from './cabinets';
import { SUPABASE_URL } from '@/lib/supabase';

export type WorkKind = 'preventive' | 'corrective' | 'inspection' | 'replacement' | 'theft_follow_up';

/*
 * The organisation's own words (Lumen-BE 0105), so neither is a closed union any more. A status
 * is whatever an administrator called a column on their board; this client has no list of them
 * and must not pretend to. What each one MEANS is `WorkStage`, which stays closed because the
 * platform owns it — see `modules/work/stages.ts` on the server.
 */
export type WorkPriority = string;
export type WorkStatus = string;

/** The eight canonical stages. Closed: the platform's axis, the one the analytics speak. */
export type WorkStage = 'open' | 'assigned' | 'acknowledged' | 'in_progress' | 'blocked' | 'review' | 'done' | 'cancelled';

export type WorkTone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/** A move the server says this caller may make. The drawer renders its buttons from these. */
export interface WorkMove {
  toStatusId: string;
  key: string;
  label: string;
  stage: WorkStage;
  icon: string;
  tone: WorkTone;
  requiresNote: boolean;
  /** Exactly one, and it is the one rendered as a button rather than hidden in a menu. */
  primary: boolean;
  /**
   * Whether taking this move raises a request instead of making it (Lumen-BE 0112).
   *
   * The button must say so — "Send for sign-off", not "Validate". A reader who presses a button
   * called Validate and finds nothing validated has been misled by the screen rather than by the
   * server, which is the one failure this flag exists to prevent.
   */
  needsApproval: boolean;
}

/* ── the approval hierarchy (Lumen-BE 0112) ──────────────────────────────────── */

export type WorkApproverKind = 'role' | 'person' | 'report_to';
export type WorkApprovalState = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface WorkApprovalLevel {
  position: number;
  /** The rung's name, or null — then the approver's own name stands for it. */
  label: string | null;
  approverKind: WorkApproverKind;
  roleId: string | null;
  roleName: string | null;
  profileId: string | null;
  profileName: string | null;
}

export interface WorkApprovalDecision {
  level: number;
  levelLabel: string | null;
  decision: 'approved' | 'rejected';
  by: WorkPerson | null;
  at: string;
  note: string | null;
}

export interface WorkApproval {
  id: string;
  state: WorkApprovalState;
  level: number;
  note: string | null;
  chain: { id: string; name: string; allowSelfApproval: boolean };
  from: { id: string; key: string; label: string };
  to: { id: string; key: string; label: string };
  requestedBy: WorkPerson | null;
  requestedAt: string;
  settledAt: string | null;
  cancelledReason: string | null;
  levels: WorkApprovalLevel[];
  decisions: WorkApprovalDecision[];
  /** The rung being asked now, resolved to the people who may sign it. Null once settled. */
  awaiting: { position: number; label: string | null; approvers: WorkPerson[] } | null;
  /** Whether THIS reader may sign it — the panel draws its buttons from exactly this. */
  can: { decide: boolean };
}
export type WorkFileType = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';

export interface WorkPerson {
  id: string;
  name: string;
}

export interface ChecklistItem {
  label: string;
  done: boolean;
  /** Who ticked it, by profile id, and when. */
  by: string | null;
  at: string | null;
}

export interface WorkOrder {
  id: string;
  /** `WO-1044`. */
  ref: string;
  number: number;
  organisationId: string;
  site: { id: string; name: string; region: string };
  cabinet: { id: string; serialNumber: string } | null;
  title: string;
  description: string | null;
  kind: WorkKind;
  /*
   * The ORGANISATION's own kind of work (Lumen-BE 0107, on the DTO since 0111), which is not
   * `kind` above. `kind` is the platform's five-word axis and is what the analytics speak; this
   * is the template the work was raised from, in the words its administrator chose. Null when
   * the work was raised by hand with no type.
   */
  typeId: string | null;
  typeLabel: string | null;
  typeAbbreviation: string | null;
  /** The equipment class the work is about (0111), from the organisation's own types. */
  category: string | null;
  priority: WorkPriority;
  priorityId: string;
  priorityLabel: string;
  priorityTone: WorkTone;
  priorityIcon: string;
  status: WorkStatus;
  statusId: string;
  statusLabel: string;
  statusTone: WorkTone;
  statusIcon: string;
  stage: WorkStage;
  /** Outstanding — the API's judgement, so no screen reasons from a status name. */
  open: boolean;
  assignee: WorkPerson | null;
  /** Who the work answers to (0111). Told and asked; it authorises nothing. */
  reportTo: WorkPerson | null;
  dueAt: string | null;
  /** The scheduled window (0111). Not the deadline — `dueAt` is still that. */
  startAt: string | null;
  endAt: string | null;
  /** The recurrence it was expanded from (0111), or null. */
  seriesId: string | null;
  /** Open, and past its due time — the API's judgement, so every screen agrees. */
  overdue: boolean;
  /* The SLA, also judged by the API: a console recomputing it from its own clock would disagree
     with the analytics by however far the two clocks differ. */
  slaTargetMinutes: number | null;
  slaDueAt: string | null;
  slaBreachedAt: string | null;
  slaState: 'compliant' | 'breached' | 'none';
  firstResponseAt: string | null;
  resolvedAt: string | null;
  labels: string[];
  checklist: ChecklistItem[];
  alarm: { id: string; name: string | null } | null;
  scheduleId: string | null;
  createdBy: WorkPerson | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  completedBy: WorkPerson | null;
  /** Bumped on every write; an edit sends it back as `If-Match`. */
  version: number;
  fileCount: number;
  /** How many comments are on it (0111) — the Kanban card shows it beside the file count. */
  commentCount: number;
}

export interface WorkEvent {
  id: number;
  kind: 'created' | 'assigned' | 'status' | 'edited' | 'checklist' | 'comment' | 'file' | string;
  /** Null: the platform — a schedule raised the work. */
  actor: WorkPerson | null;
  note: string | null;
  detail: unknown;
  createdAt: string;
}

export interface WorkFile {
  id: string;
  fileName: string;
  contentType: WorkFileType;
  sizeBytes: number;
  uploadedBy: WorkPerson | null;
  storedAt: string | null;
  /** Signed, under the Storage root, valid for minutes: fetch it, never keep it. */
  signedPath: string;
}

export interface WorkOrderDetail extends WorkOrder {
  events: WorkEvent[];
  files: WorkFile[];
  can: { manage: boolean; update: boolean };
  /**
   * The legal moves, primary first.
   *
   * The drawer renders its buttons from this rather than from a table kept here, so the screen
   * cannot offer a move the API would refuse — the same contract `AlarmDetail.actions` makes.
   */
  moves: WorkMove[];
  /**
   * Every sign-off ever asked about this work order, newest first (Lumen-BE 0112).
   *
   * At most one is `pending`. The settled ones stay because they are the evidence of who
   * authorised work on live plant, and a panel showing only the open request would leave that
   * evidence reachable from nowhere.
   */
  approvals: WorkApproval[];
}

/**
 * What the list may be narrowed by.
 *
 * Every facet is COMMA-SEPARATED and takes several values at once (Lumen-BE 0111 for `typeId`
 * and `assignee`; the rest since 0105). That matters more than it looks: the board's menus are
 * checkbox lists, and a facet the API could only take one of meant the console narrowing its own
 * loaded page instead — describing a page while appearing to describe the set.
 */
export interface WorkFilters {
  status?: string;
  priority?: string;
  /** The canonical stage, which every organisation shares. */
  stage?: string;
  typeId?: string;
  kind?: string;
  /** `me`, `none` and people, in any combination — `none` is an OR with the ids, not an AND. */
  assignee?: string;
  /** The due-date window, local ISO days, inclusive. Undated work is outside every window. */
  dueFrom?: string;
  dueTo?: string;
  siteId?: string;
  cabinetId?: string;
  alarmId?: string;
  scheduleId?: string;
  overdue?: 'true';
  q?: string;
  limit?: number;
}

export interface CreateWorkInput {
  siteId: string;
  cabinetId?: string | null;
  title: string;
  description?: string | null;
  kind: WorkKind;
  priority: WorkPriority;
  assigneeId?: string | null;
  dueAt?: string | null;
  labels?: string[];
  checklist?: string[];
  alarmId?: string | null;
}

export type PatchWorkInput = Partial<{
  title: string;
  description: string | null;
  kind: WorkKind;
  /** The template to raise it from (Lumen-BE 0107). */
  typeId: string;
  /* What 0111 added. Changing any of them needs `work.manage`, like the assignee. */
  category: string | null;
  reportToId: string | null;
  startAt: string | null;
  endAt: string | null;
  /** Everyone else on it, stated in full: the list sent is the list kept. */
  team: string[];
  priority: WorkPriority;
  priorityId: string;
  /** The key form, still accepted. `statusId` is what the drawer's moves send. */
  status: WorkStatus;
  statusId: string;
  /** Why, when the move the organisation configured asks for a reason. */
  note: string;
  assigneeId: string | null;
  dueAt: string | null;
  cabinetId: string | null;
  labels: string[];
  checklist: Array<{ label: string; done: boolean }>;
}>;

export const workKeys = {
  all: ['work'] as const,
  list: (f: WorkFilters) => ['work', 'list', f] as const,
  detail: (id: string) => ['work', 'detail', id] as const,
};

const refreshLists = (qc: QueryClient): void => void qc.invalidateQueries({ queryKey: ['work', 'list'] });

export function useWorkOrders(filters: WorkFilters = {}, enabled = true) {
  return useQuery({
    queryKey: workKeys.list(filters),
    enabled,
    queryFn: () => api.get<{ data: WorkOrder[]; truncated: boolean }>('/work-orders', { ...filters } as Record<string, string | number>),
    /* Work moves at human speed; a minute keeps My work and the NOC honest without a stream. */
    refetchInterval: 60_000,
  });
}

export function useWorkOrder(id: string | null) {
  return useQuery({
    queryKey: workKeys.detail(id ?? 'none'),
    enabled: id !== null,
    queryFn: () => api.get<{ work: WorkOrderDetail }>(`/work-orders/${id}`),
    select: (r) => r.work,
  });
}

export function useCreateWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkInput) => api.post<{ work: WorkOrderDetail }>('/work-orders', input),
    onSuccess: (r) => {
      qc.setQueryData(workKeys.detail(r.work.id), r);
      refreshLists(qc);
    },
  });
}

/** One change; `version` is what the editor read — a work order changed since is refused (409). */
/**
 * Move any work order, from anywhere — the Kanban's drop and the card's move menu.
 *
 * `useUpdateWork` is bound to one id because the drawer only ever edits the order it is showing.
 * A board moves whichever card was dragged, so it needs the id per call, and it uses the API's
 * dedicated transition endpoint rather than a PATCH: the action is data the client already has.
 */
export function useMoveWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, toStatusId, note }: { id: string; version: number; toStatusId: string; note?: string }) =>
      request<{ work: WorkOrderDetail }>(`/work-orders/${id}/moves`, {
        method: 'POST',
        body: { toStatusId, ...(note === undefined ? {} : { note }) },
        headers: { 'If-Match': String(version) },
      }),
    onSuccess: (r) => {
      qc.setQueryData(workKeys.detail(r.work.id), r);
      refreshLists(qc);
      /* The column counts are the server's, so they have to be asked again. */
      void qc.invalidateQueries({ queryKey: ['work', 'board'] });
    },
  });
}

/**
 * Sign, or refuse, the move a work order is waiting for.
 *
 * No `If-Match`: a signature is not an edit of the work order, and the version the panel read
 * says nothing about whether the REQUEST is still the one being asked. The server locks the
 * request itself and refuses a stale one by name — a work order moved underneath it comes back
 * as a conflict with a sentence saying where it went.
 */
export function useDecideApproval(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { decision: 'approved' | 'rejected'; note?: string }) =>
      api.post<{ work: WorkOrderDetail; approval: WorkApproval; moved: { toKey: string; toLabel: string } | null }>(
        `/work-orders/${id}/approval`,
        body,
      ),
    onSuccess: (r) => {
      qc.setQueryData(workKeys.detail(r.work.id), { work: r.work });
      refreshLists(qc);
      /* The last signature moves the work order, so the column counts are no longer ours. */
      void qc.invalidateQueries({ queryKey: ['work', 'board'] });
    },
  });
}

export function useUpdateWork(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, patch }: { version: number; patch: PatchWorkInput }) =>
      request<{ work: WorkOrderDetail }>(`/work-orders/${id}`, { method: 'PATCH', body: patch, headers: { 'If-Match': String(version) } }),
    onSuccess: (r) => {
      qc.setQueryData(workKeys.detail(id), r);
      refreshLists(qc);
    },
  });
}

/**
 * The same PATCH, for a screen that edits ROWS rather than one work order.
 *
 * `useUpdateWork` closes over an id, which a table cannot use: a hook per row breaks the rules
 * of hooks the moment the rows change. This takes the id per call instead. Everything else is
 * identical, `If-Match` included — a list is exactly where two people edit the same row at once,
 * so the version check matters more here, not less.
 */
export function useUpdateAnyWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, patch }: { id: string; version: number; patch: PatchWorkInput }) =>
      request<{ work: WorkOrderDetail }>(`/work-orders/${id}`, { method: 'PATCH', body: patch, headers: { 'If-Match': String(version) } }),
    onSuccess: (r) => {
      qc.setQueryData(workKeys.detail(r.work.id), r);
      refreshLists(qc);
    },
  });
}

export function useCommentWork(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<{ work: WorkOrderDetail }>(`/work-orders/${id}/comments`, { note }),
    onSuccess: (r) => qc.setQueryData(workKeys.detail(id), r),
  });
}

const STORAGE_ROOT = `${SUPABASE_URL.replace(/\/+$/, '')}/storage/v1`;

/** The API's three steps, as for a cabinet picture: a signed path, the file to Storage, a confirmation. */
export async function uploadWorkFile(workId: string, file: File): Promise<WorkFile[]> {
  const started = await api.post<{ fileId: string; uploadPath: string }>(`/work-orders/${workId}/files`, {
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  let res: Response;
  try {
    res = await fetch(`${STORAGE_ROOT}${started.uploadPath}`, { method: 'PUT', headers: { 'content-type': file.type }, body: file });
  } catch {
    throw new StorageUploadError(0);
  }
  if (!res.ok) throw new StorageUploadError(res.status);
  const done = await api.post<{ files: WorkFile[] }>(`/work-orders/${workId}/files/${started.fileId}/complete`, {});
  return done.files;
}

export function useWorkFiles(id: string) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: workKeys.detail(id) });
  return {
    upload: useMutation({ mutationFn: (file: File) => uploadWorkFile(id, file), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (fileId: string) => api.del<{ files: WorkFile[] }>(`/work-orders/${id}/files/${fileId}`), onSuccess: refresh }),
  };
}

/** A stored file's bytes, as a blob a link or an image can show (the console's CSP admits `blob:`). */
export async function fetchWorkFile(signedPath: string): Promise<Blob> {
  const res = await fetch(`${STORAGE_ROOT}${signedPath}`);
  if (!res.ok) throw new StorageUploadError(res.status);
  return res.blob();
}

/* ───────────────────────────────────────────── periodic maintenance (Lumen-BE 0093) ── */

export interface MaintenanceSchedule {
  id: string;
  organisationId: string;
  name: string;
  target: { kind: 'type'; cabinetType: string } | { kind: 'cabinet'; cabinetId: string; serialNumber: string };
  everyDays: number;
  leadDays: number;
  priority: WorkPriority;
  assignee: WorkPerson | null;
  checklist: string[];
  active: boolean;
  cabinets: number;
  openWork: number;
  overdueWork: number;
  nextDueAt: string | null;
  version: number;
}

export interface DueVisit {
  scheduleId: string;
  scheduleName: string;
  organisationId: string;
  cabinetId: string;
  serialNumber: string;
  siteId: string;
  siteName: string;
  dueAt: string;
  raiseFrom: string;
  openWork: { id: string; ref: string; status: string } | null;
  lastDoneAt: string | null;
}

export interface ScheduleInput {
  name: string;
  cabinetType?: string | null;
  cabinetId?: string | null;
  everyDays: number;
  leadDays: number;
  priority: WorkPriority;
  assigneeId?: string | null;
  checklist: string[];
  active?: boolean;
}

const scheduleKeys = {
  list: ['work', 'schedules'] as const,
  due: (f: { scheduleId?: string; cabinetId?: string }) => ['work', 'due', f] as const,
};

export function useSchedules(enabled = true) {
  return useQuery({
    queryKey: scheduleKeys.list,
    enabled,
    queryFn: () => api.get<{ data: MaintenanceSchedule[] }>('/maintenance-schedules'),
    select: (r) => r.data,
  });
}

export function useDueVisits(filter: { scheduleId?: string; cabinetId?: string }, enabled = true) {
  return useQuery({
    queryKey: scheduleKeys.due(filter),
    enabled,
    queryFn: () => api.get<{ data: DueVisit[] }>('/maintenance/due', { ...filter } as Record<string, string>),
    select: (r) => r.data,
  });
}

const refreshMaintenance = (qc: QueryClient): void => void qc.invalidateQueries({ queryKey: ['work'] });

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleInput) => api.post<{ schedule: MaintenanceSchedule }>('/maintenance-schedules', input),
    onSuccess: () => refreshMaintenance(qc),
  });
}

export function useUpdateSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, patch }: { version: number; patch: Partial<ScheduleInput> }) =>
      request<{ schedule: MaintenanceSchedule }>(`/maintenance-schedules/${id}`, { method: 'PATCH', body: patch, headers: { 'If-Match': String(version) } }),
    onSuccess: () => refreshMaintenance(qc),
  });
}

/** *Raise due work now*: the job's step at once, for the organisations the caller reaches. */
export function useRaiseDueWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ raised: number; mailed: number; refs: string[] }>('/maintenance/run', {}),
    onSuccess: () => refreshMaintenance(qc),
  });
}
